import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './HeroSection.css';

interface HeroSectionProps {
  onBooking: () => void;
}

export default function HeroSection({ onBooking }: HeroSectionProps) {
  const [visible, setVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const parallaxOffset = scrollY * 0.35;

  return (
    <section
      ref={heroRef}
      id="inicio"
      className="hero-section relative w-full flex items-center justify-center overflow-hidden"
    >
      {/* Background image with parallax */}
      <div
        className="absolute inset-0 scale-110 hero-parallax-bg"
        style={{ transform: `translateY(${parallaxOffset}px) scale(1.1)` }}
      >
        <img
          src="https://readdy.ai/api/search-image?query=ultra%20premium%20luxury%20barbershop%20interior%20dark%20moody%20cinematic%20atmosphere%20black%20leather%20barber%20chairs%20ornate%20mirrors%20dramatic%20side%20lighting%20deep%20shadows%20elegant%20upscale%20environment%20marble%20surfaces%20dark%20walls%20minimal%20decor%20high%20contrast%20monochromatic%20tones%20professional%20photography&width=1920&height=1080&seq=hero-premium-01&orientation=landscape"
          alt="Barber Studio Luisfer"
          className="w-full h-full object-cover object-center"
        />
      </div>

      {/* Layered overlays */}
      <div className="absolute inset-0 bg-black/65" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80" />

      {/* Subtle metallic glow — top center */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] pointer-events-none hero-metallic-glow"
      />

      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

      {/* Content */}
      <div className="relative z-10 w-full flex flex-col items-center justify-center text-center px-4 md:px-8">

        {/* Label */}
        <p
          className={`hero-label mb-6 transition-all duration-1000 delay-100 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          Barranquilla · Colombia
        </p>

        {/* Main headline */}
        <h1
          className={`hero-headline mb-5 transition-all duration-1000 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          Más que una barbería.
          <br />
          <span className="hero-gradient-text">
            Una experiencia.
          </span>
        </h1>

        {/* Divider */}
        <div
          className={`w-16 h-px bg-gradient-to-r from-transparent via-[#D9D9D9] to-transparent mb-6 transition-all duration-1000 delay-300 ${
            visible ? 'opacity-100' : 'opacity-0'
          }`}
        />

        {/* Subheadline */}
        <p
          className={`hero-subheadline max-w-xl mx-auto mb-10 transition-all duration-1000 delay-300 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          Precisión, estilo y atención premium en cada detalle.
        </p>

        {/* CTA Buttons */}
        <div
          className={`flex flex-col sm:flex-row gap-4 items-center justify-center transition-all duration-1000 delay-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <Link
            to="/reservar"
            className="hero-cta-primary cursor-pointer"
          >
            Reservar cita
          </Link>
          <button
            onClick={() => scrollTo('#servicios')}
            className="hero-cta-secondary cursor-pointer"
          >
            Ver servicios
          </button>
        </div>

        {/* Stats */}
        <div
          className={`mt-16 flex flex-wrap justify-center gap-10 md:gap-20 transition-all duration-1000 delay-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          {[
            { value: '10+', label: 'Años de experiencia' },
            { value: '2K+', label: 'Clientes satisfechos' },
            { value: '100%', label: 'Calidad premium' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="hero-stat-value">
                {stat.value}
              </p>
              <p className="hero-stat-label">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-all duration-1000 delay-1000 ${
          visible ? 'opacity-60' : 'opacity-0'
        }`}
      >
        <div className="w-px h-10 bg-gradient-to-b from-white/50 to-transparent animate-pulse" />
        <i className="ri-arrow-down-s-line text-white/50 text-lg w-5 h-5 flex items-center justify-center" />
      </div>
    </section>
  );
}